import { tokenFetcher } from "@/services/API/API.service";

import { ROUTE_LOGIN_OUT } from "@/constants/routes.constants";
import i18n from "@/i18n";
import { locForSetClientConfig } from "@/models/localForage/localForage.model";
import { LoginResponseData, loginResponseDataSchema, Tokens } from "@/types";
import { validateWithSchema } from "@/utils/dataValidators.zod";

// #region fetchJwt
// Give BE RefreshToken, get back JWT (R + Auth)
export async function fetchJwt(
  refreshToken: string,
  config?: boolean
): Promise<
  | {
      rStatus: "success";
      data: LoginResponseData;
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  const response = await tokenFetcher({
    route: "/auth/refreshToken",
    method: "POST",
    options: { body: { refreshToken, config } },
  });
  if (response.status === 200) {
    // validate tokens
    const validatedLoginResponse = validateWithSchema<LoginResponseData>(
      response.data,
      loginResponseDataSchema,
      "Tokens"
    );

    if (!validatedLoginResponse) {
      return {
        rStatus: "error",
        message: "Invalid tokens",
      };
    }

    if (validatedLoginResponse.config) {
      await locForSetClientConfig(validatedLoginResponse.config);
    } else {
      console.log("No config in login response");
    }

    return {
      rStatus: "success",
      data: validatedLoginResponse,
    };
  } else {
    console.log("Refresh token failed");
    window.location.href = ROUTE_LOGIN_OUT;
    return {
      rStatus: "error",
      message: "Refresh token failed",
    };
  }
}
// #endregion fetchJwt

// #region requestEmail
// Give BE encoded Email, get back JWT (R + Auth)
export async function requestEmail(encodedEmail: string): Promise<number> {
  console.log("HIT REQUEST EMAIL");

  const response = await tokenFetcher({
    route: "/auth/magicLink",
    method: "POST",
    options: { body: { encodedEmail: encodedEmail } },
  });

  console.log(response, "RESPONSE");
  if (response.status === 202) {
    return response.status;
  } else if (response.status === 400) {
    console.log("Contact admin");
    return response.status;
  } else if (response.status === 404) {
    console.log("Email not sent");
    return response.status;
  } else {
    console.log("Email not sent");
    return response.status;
  }
}
// #endregion requestEmail

// #region mfaLogin
// Give BE encoded Email, Pw, Code, get back JWT (R + Auth)
export async function mfaLogin(encodedMFAConfirmString: string): Promise<
  | {
      rStatus: "success";
      tokens: Tokens;
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  const response = await tokenFetcher({
    route: "/auth/login",
    method: "POST",
    options: { body: { loginToken: encodedMFAConfirmString } },
  });

  console.log(response, "RESPONSE");
  if (response.status === 200) {
    const validatedLoginResponse = validateWithSchema<LoginResponseData>(
      response.data,
      loginResponseDataSchema,
      "LoginResponseData"
    );

    if (!validatedLoginResponse) {
      return {
        rStatus: "error",
        message: i18n.t("Invalid login response"),
      };
    }

    if (validatedLoginResponse.config) {
      await locForSetClientConfig(validatedLoginResponse.config);
    } else {
      console.warn("mfaLogin: No config in login response");
    }

    console.log("MFA CONFIRM sent");
    return {
      rStatus: "success",
      tokens: {
        authToken: validatedLoginResponse.authToken,
        refreshToken: validatedLoginResponse.refreshToken,
      },
    };
  }

  if (response.status === 400) {
    console.error("mfaLogin: Bad request");
    return {
      rStatus: "error",
      message: i18n.t("Bad request. Please contact support"),
    };
  }

  if (response.status === 204) {
    console.error("mfaLogin: No user found");
    return {
      rStatus: "error",
      message: i18n.t("Invalid login. Please check your credentials"),
    };
  }

  if (response.status === 401 || response.status === 403) {
    console.error("mfaLogin: Unauthorized or forbidden");
    return {
      rStatus: "error",
      message: i18n.t("Invalid login. Please check your credentials"),
    };
  }

  if (response.status === 500) {
    console.error("mfaLogin: Internal server error");
    return {
      rStatus: "error",
      message: i18n.t("Internal server error"),
    };
  }

  if (response.status === 0) {
    console.error("mfaLogin: Network error");
    return {
      rStatus: "error",
      message: i18n.t("Network error. Please try again later"),
    };
  }

  return {
    rStatus: "error",
    message: i18n.t("Failed to authenticate"),
  };
}
// #endregion mfaLogin
